import type { DxfEntityKey, DxfPoint } from '../dxf/types';

/**
 * Wire shapes for
 * `GET /parts/files/:uuid/geometric_analysis_data` and
 * `POST /parts/files/bulk_geometric_analysis_data`.
 *
 * Feature and feedback rows carry whichever geometry references their source
 * produced: CAD interrogation fills `faces`/`edges`/`viewpoint`, DXF
 * interrogation fills `entityKeys` (and `spans` on gap feedback). Either set
 * may be empty — some items (e.g. `no_cut_geometry`) reference no geometry at
 * all. Viewer-prepared features, feedback, and setups are not part of this
 * contract.
 *
 * NB `@frontend/partviewer` imports this module by path
 * (`@frontend/api/src/parts/geometricAnalysis/types`) rather than through the
 * package barrel, so keep its imports type-only.
 */

export const GEOMETRIC_ANALYSIS_FEEDBACK_LEVEL = {
  COST_DRIVER: 'cost_driver',
  MANUFACTURING_ISSUE: 'manufacturing_issue',
  COMPLETE_EXCLUSION: 'complete_exclusion',
  INFORMATION: 'information',
} as const;

export type GeometricAnalysisFeedbackLevel =
  (typeof GEOMETRIC_ANALYSIS_FEEDBACK_LEVEL)[keyof typeof GEOMETRIC_ANALYSIS_FEEDBACK_LEVEL];

/** How a `dbData` value is interpreted and formatted; mirrors pint `FeaturePropTypes`. */
export const GEOMETRIC_ANALYSIS_PROP_TYPE = {
  LENGTH: 'length',
  ANGLE: 'angle',
  BOOL: 'bool',
  STRING: 'string',
  NON_DIMENSIONAL_NUMERIC: 'non_dimensional',
  POINT_VECTOR: 'point_vector',
  UNIT_VECTOR: 'unit_vector',
  ABSTRACT: 'abstract',
} as const;

export type GeometricAnalysisPropType =
  (typeof GEOMETRIC_ANALYSIS_PROP_TYPE)[keyof typeof GEOMETRIC_ANALYSIS_PROP_TYPE];

/** Plain JSON, with mutable arrays so partviewer's key-casing helpers accept it. */
export type GeometricAnalysisJsonValue =
  | string
  | number
  | boolean
  | null
  | GeometricAnalysisJsonValue[]
  | { [key: string]: GeometricAnalysisJsonValue };

export type GeometricAnalysisVector3 = {
  x: number;
  y: number;
  z: number;
};

/** Camera pose for snapping a 3D viewer to a CAD feature. */
export type GeometricAnalysisViewpoint = {
  position: GeometricAnalysisVector3;
  target: GeometricAnalysisVector3;
};

/**
 * One displayable property row. Values are in the interrogation's base units
 * (mm for DXF interrogation); formatting to display units happens client-side.
 */
export type GeometricAnalysisDbDatum = {
  name: string;
  /** Dimensional power for `length` props (1 length, 2 area, 3 volume); null otherwise. */
  order: number | null;
  type: GeometricAnalysisPropType;
  value: number | boolean | string | [number, number, number];
};

/**
 * A measured gap on a DXF drawing, as a line segment in drawing units.
 */
export type GeometricAnalysisSpan = readonly [DxfPoint, DxfPoint];

type GeometricAnalysisItemBase = {
  additionalViewerContentInfo: { [key: string]: GeometricAnalysisJsonValue };
  authenticatedUrl: string | null;
  dbData: GeometricAnalysisDbDatum[];
  edges: number[];
  /** Sorted DXF mapping keys of the source entities; empty for CAD rows. */
  entityKeys: DxfEntityKey[];
  faces: number[];
  id: number;
  isInnate: boolean;
  name: string;
  /** Part file uuid the item belongs to. */
  referenceUuid: string;
  viewpoint: GeometricAnalysisViewpoint | null;
};

export type GeometricAnalysisFeature = GeometricAnalysisItemBase & {
  flatFeatureId: number | null;
};

export type GeometricAnalysisFeedbackItem = GeometricAnalysisItemBase & {
  flatFeedbackItemId: number | null;
  level: GeometricAnalysisFeedbackLevel;
  /** Gap segments for clearance feedback; empty otherwise. */
  spans: GeometricAnalysisSpan[];
  transform: number[] | null;
};

export type GeometricAnalysisInterrogationFile = {
  authenticatedUrl: string | null;
  documentHash: string | null;
  featureIds: number[];
  feedbackIds: number[];
  id: number;
  partFileUuid: string;
  referenceUuid: string;
  setupIds: number[];
  task: string;
  version: string | null;
};

export type GeometricAnalysisSetup = {
  featureIds: number[];
  feedbackIds: number[];
  id: number;
  /** Null when the setup is not attached to an interrogation file. */
  interrogationFileId: number | null;
  position: number;
  referenceUuid: string;
  type: string;
};

export type PartFileGeometricAnalysisData = {
  features: GeometricAnalysisFeature[];
  feedbackItems: GeometricAnalysisFeedbackItem[];
  interrogationFiles: GeometricAnalysisInterrogationFile[];
  setups: GeometricAnalysisSetup[];
  /** Part file uuid. Present on both the single-file GET and each bulk row. */
  uuid: string;
};

export type BulkPartFileGeometricAnalysisData =
  PartFileGeometricAnalysisData & {
    uuid: string;
  };
